export type GameType =
  | "tanks"
  | "trivia"
  | "quickdraw"
  | "midnight-alibis"
  | "racing"
  | "dungeon-relay"
  | "color-echo"
  | "cupcake-stack"
  | "critter-crossing"
  | "firefly-friends"
  | "fuse-runners"
  | "runway-rookies"
  | "roshambo-showdown";
export type StartPolicy = "host" | "automatic";
export type InProgressJoinPolicy = "immediate" | "nextCheckpoint" | "nextSession" | "closed";

export interface GameVariantDefinition {
  id: string;
  title: string;
  description: string;
}

export interface AttractPreviewDefinition {
  tagline: string;
  expectedSessionMinutes: number;
  accentColor: string;
  background: string;
}

export interface LeaderboardThemeDefinition {
  boardId: string;
  title: string;
  accentColor: string;
  background: string;
}

/**
 * Client-safe platform metadata. Server factories and client renderers register at
 * their own seams using the same stable game id; they never enter this shared bundle.
 */
export interface GameDefinition<TGame extends GameType = GameType> {
  id: TGame;
  title: string;
  description: string;
  minParticipants: number;
  maxParticipants: number;
  startPolicy: StartPolicy;
  inProgressJoinPolicy: InProgressJoinPolicy;
  requiresSharedDisplay: boolean;
  variants: readonly GameVariantDefinition[];
  attract: AttractPreviewDefinition;
  leaderboards: readonly LeaderboardThemeDefinition[];
}

export const GAME_DEFINITIONS = {
  tanks: {
    id: "tanks",
    title: "3D Tanks",
    description: "Team up in a neon arena and survive waves of enemy tanks.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "immediate",
    requiresSharedDisplay: false,
    variants: [
      {
        id: "campaign",
        title: "Campaign",
        description: "Clear handcrafted levels together.",
      },
      {
        id: "survival",
        title: "Survival",
        description: "Hold out against increasingly dangerous waves.",
      },
    ],
    attract: {
      tagline: "Grab a phone. Join the squad. Blast some tanks.",
      expectedSessionMinutes: 10,
      accentColor: "#38bdf8",
      background: "linear-gradient(135deg, #07111f, #0f3b5f)",
    },
    leaderboards: [
      {
        boardId: "best-runs",
        title: "Best Tank Runs",
        accentColor: "#38bdf8",
        background: "linear-gradient(135deg, #07111f, #12304a)",
      },
    ],
  },
  trivia: {
    id: "trivia",
    title: "TV Trivia",
    description: "Race friends to answer fast and climb the standings.",
    minParticipants: 2,
    maxParticipants: 8,
    startPolicy: "automatic",
    inProgressJoinPolicy: "nextCheckpoint",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Scan in. Think fast. Rule the room.",
      expectedSessionMinutes: 8,
      accentColor: "#f472b6",
      background: "linear-gradient(135deg, #25103c, #6d1c67)",
    },
    leaderboards: [
      {
        boardId: "champions",
        title: "Trivia Champions",
        accentColor: "#f472b6",
        background: "linear-gradient(135deg, #25103c, #5b215f)",
      },
    ],
  },
  quickdraw: {
    id: "quickdraw",
    title: "Quick Draw",
    description: "Hold steady, wait for DRAW, and beat the room to the button.",
    minParticipants: 2,
    maxParticipants: 8,
    startPolicy: "host",
    inProgressJoinPolicy: "nextCheckpoint",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Hold steady. Wait for DRAW. Fastest thumb wins.",
      expectedSessionMinutes: 3,
      accentColor: "#f59e0b",
      background: "linear-gradient(135deg, #2b1608, #9a3412)",
    },
    leaderboards: [
      {
        boardId: "fastest-guns",
        title: "Fastest Guns",
        accentColor: "#f59e0b",
        background: "linear-gradient(135deg, #2b1608, #78350f)",
      },
    ],
  },
  "midnight-alibis": {
    id: "midnight-alibis",
    title: "Midnight Alibis",
    description: "Unmask a murderer while keeping your own suspicious secret buried.",
    minParticipants: 3,
    maxParticipants: 8,
    startPolicy: "host",
    inProgressJoinPolicy: "nextSession",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Everyone has a secret. One of you is a murderer.",
      expectedSessionMinutes: 10,
      accentColor: "#d4af37",
      background: "linear-gradient(135deg, #120f18, #4a1728)",
    },
    leaderboards: [
      {
        boardId: "detective-accuracy",
        title: "Detective Accuracy",
        accentColor: "#d4af37",
        background: "linear-gradient(135deg, #120f18, #4a1728)",
      },
      {
        boardId: "successful-escapes",
        title: "Successful Escapes",
        accentColor: "#c02654",
        background: "linear-gradient(135deg, #180b10, #5b1027)",
      },
    ],
  },
  racing: {
    id: "racing",
    title: "Pocket Rally",
    description: "Race RC cars on the TV and tune your build between races on your phone.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "nextSession",
    requiresSharedDisplay: true,
    variants: [
      {
        id: "championship",
        title: "Championship",
        description: "Six races, private garages, weapons, and a final podium.",
      },
    ],
    attract: {
      tagline: "Your phone is the controller. The whole room is the racetrack.",
      expectedSessionMinutes: 15,
      accentColor: "#f97316",
      background: "linear-gradient(135deg, #17230f, #8a3b12)",
    },
    leaderboards: [
      {
        boardId: "championship-points",
        title: "Pocket Rally Champions",
        accentColor: "#f97316",
        background: "linear-gradient(135deg, #17230f, #7c2d12)",
      },
      {
        boardId: "fastest-championship",
        title: "Fastest Championships",
        accentColor: "#facc15",
        background: "linear-gradient(135deg, #27210b, #854d0e)",
      },
    ],
  },
  "dungeon-relay": {
    id: "dungeon-relay",
    title: "Dungeon Relay",
    description: "Choose a hero, break the monster rifts, and escape together.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "nextCheckpoint",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Four heroes. One screen. Break the rifts and get out alive.",
      expectedSessionMinutes: 15,
      accentColor: "#a78bfa",
      background: "linear-gradient(135deg, #130f24, #3b1d54)",
    },
    leaderboards: [
      {
        boardId: "deepest-delves",
        title: "Deepest Delves",
        accentColor: "#a78bfa",
        background: "linear-gradient(135deg, #130f24, #3b1d54)",
      },
    ],
  },
  "color-echo": {
    id: "color-echo",
    title: "Color Echo",
    description: "Watch the lights, hear the tones, and repeat the growing pattern.",
    minParticipants: 1,
    maxParticipants: 8,
    startPolicy: "host",
    inProgressJoinPolicy: "nextCheckpoint",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Watch the lights. Hear the tones. Echo the pattern.",
      expectedSessionMinutes: 6,
      accentColor: "#22d3ee",
      background: "linear-gradient(135deg, #111827, #164e63)",
    },
    leaderboards: [
      {
        boardId: "longest-echoes",
        title: "Longest Echoes",
        accentColor: "#22d3ee",
        background: "linear-gradient(135deg, #111827, #164e63)",
      },
    ],
  },
  "cupcake-stack": {
    id: "cupcake-stack",
    title: "Cupcake Stack",
    description: "Catch cake, dodge kitchen junk, and build the tallest silly cupcake.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "nextSession",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Catch the cake. Dodge the socks. Stack it sky-high.",
      expectedSessionMinutes: 3,
      accentColor: "#fb7185",
      background: "linear-gradient(135deg, #3b1532, #9f3c62)",
    },
    leaderboards: [],
  },
  "critter-crossing": {
    id: "critter-crossing",
    title: "Critter Crossing",
    description: "Guide tiny garden friends past puddles, sprinklers, and rolling toys.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "nextCheckpoint",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Tiny paws. Big garden. Cross together!",
      expectedSessionMinutes: 7,
      accentColor: "#84cc16",
      background: "linear-gradient(135deg, #16351f, #4d7c0f)",
    },
    leaderboards: [],
  },
  "firefly-friends": {
    id: "firefly-friends",
    title: "Firefly Friends",
    description: "Guide glowing fireflies into matching lanterns and bloom the garden together.",
    minParticipants: 1,
    maxParticipants: 8,
    startPolicy: "host",
    inProgressJoinPolicy: "nextSession",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Match the glow. Guide them home. Bloom together.",
      expectedSessionMinutes: 4,
      accentColor: "#facc15",
      background: "linear-gradient(135deg, #11153b, #14532d)",
    },
    leaderboards: [],
  },
  "fuse-runners": {
    id: "fuse-runners",
    title: "Fuse Runners",
    description: "Break the arena, power up, and outlast three explosive Rivals.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "immediate",
    requiresSharedDisplay: true,
    variants: [
      {
        id: "blastTour",
        title: "Blast Tour",
        description: "Five arenas with computer Rivals filling every open Seat.",
      },
    ],
    attract: {
      tagline: "Drop a spark. Find an escape. Be the last Runner standing.",
      expectedSessionMinutes: 8,
      accentColor: "#fbbf24",
      background: "linear-gradient(135deg, #17231f, #7c2d12)",
    },
    leaderboards: [],
  },
  "runway-rookies": {
    id: "runway-rookies",
    title: "Runway Rookies",
    description: "Guide a friendly plane through three calm landing challenges.",
    minParticipants: 1,
    maxParticipants: 4,
    startPolicy: "host",
    inProgressJoinPolicy: "nextSession",
    requiresSharedDisplay: true,
    variants: [
      { id: "calm-skies", title: "Calm Skies", description: "Very gentle with no rain or night." },
      {
        id: "weather-adventure",
        title: "Weather Adventure",
        description: "A little rain and a friendly night flight.",
      },
      {
        id: "ace-approaches",
        title: "Ace Approaches",
        description: "Stronger wind and a shorter wet runway.",
      },
    ],
    attract: {
      tagline: "Your phone is the controls. Bring it in smooth.",
      expectedSessionMinutes: 7,
      accentColor: "#38bdf8",
      background: "linear-gradient(180deg, #7dd3fc, #d9f99d)",
    },
    leaderboards: [],
  },
  "roshambo-showdown": {
    id: "roshambo-showdown",
    title: "Roshambo Showdown",
    description: "Battle in rapid Rock-Paper-Scissors rounds until only one champion remains.",
    minParticipants: 2,
    maxParticipants: 8,
    startPolicy: "host",
    inProgressJoinPolicy: "nextSession",
    requiresSharedDisplay: true,
    variants: [],
    attract: {
      tagline: "Tap. Clash. Outlast. Rock, Paper, Scissors Battle Royale.",
      expectedSessionMinutes: 4,
      accentColor: "#a855f7",
      background: "linear-gradient(135deg, #1e1b4b, #4c1d95)",
    },
    leaderboards: [
      {
        boardId: "championships",
        title: "Roshambo Championships",
        accentColor: "#a855f7",
        background: "linear-gradient(135deg, #1e1b4b, #4c1d95)",
      },
    ],
  },
} as const satisfies { [TGame in GameType]: GameDefinition<TGame> };

export function gameDefinition<TGame extends GameType>(gameType: TGame): GameDefinition<TGame> {
  return GAME_DEFINITIONS[gameType] as unknown as GameDefinition<TGame>;
}

export function gameCatalog(): readonly GameDefinition[] {
  return Object.values(GAME_DEFINITIONS);
}

export function isGameType(value: unknown): value is GameType {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(GAME_DEFINITIONS, value);
}

export function gameVariantTitle(gameType: GameType, variantId?: string): string | null {
  if (!variantId) return null;
  return (
    gameDefinition(gameType).variants.find((variant) => variant.id === variantId)?.title ?? null
  );
}
